// ============================================================
// ImportDialog - Modal for previewing and selecting what to import
// from a parsed session file. Shows checkboxes for each present
// section, item counts, and normalization warnings.
// ============================================================
import { useState } from 'react';
import type { Tab, AtkProfiles } from '../types';
import type { ValidationWarning } from '../engine/importValidation';
import common from '../styles/common.module.css';
import { countOf } from '../utils/countOf';
import { SectionFooter, SectionRows, type SectionRowSpec } from './ImportExportParts';
import styles from '../styles/ImportExportDialog.module.css';
import useModalBackdrop from '../hooks/useModalBackdrop';
import useEscapeKey from '../hooks/useEscapeKey.js';

export interface ImportSelections {
  attackProfiles: boolean;
  targetTabs: boolean;
  targetTemplates: boolean;
  narrativeLogs: boolean;
  /** v4: the roster itself. Supersedes attackProfiles/targetTabs. */
  roster: boolean;
  /** v4: bring wounds/conditions/ammo across, or start them fresh. */
  combatState: boolean;
  /**
   * THE BATTLEFIELD, AND WHERE EVERYONE WAS STANDING.
   *
   * These four were read by the importer and never offered here, so
   * `selections.battlefield` was `undefined` - falsy - and the map, the map
   * library, every token position and every waiting room were DISCARDED on
   * import however complete the file was. Reported from play as "a
   * default-sized blank map with all characters in a ring" (2026-08-19).
   */
  battlefield: boolean;
  mapLibrary: boolean;
  tokenPositions: boolean;
  waitingRooms: boolean;
  /** v8.363.10 - handouts come back as the GM's drafts; a file card says it was not carried. */
  handouts: boolean;
}

export interface ParsedImportData {
  atkProfiles: AtkProfiles | null;
  tabs: Tab[] | null;
  tgtTemplates: Record<string, unknown> | null;
  hasNarrative: boolean;
  /** v4 only - the combatants the file carries. */
  roster?: { id: string; name: string }[] | null;
  hasCombatState?: boolean;
  /**
   * What the battlefield half of the file holds. The parser has always
   * reported these; the dialog never asked for them, which is how the map
   * and every placement came to be dropped on import.
   */
  hasBattlefield?: boolean;
  mapCount?: number;
  tokenCount?: number;
  roomCount?: number;
  handoutCount?: number;
  warnings: ValidationWarning[];
  version: number;
}

interface Props {
  data: ParsedImportData;
  onImport: (selections: ImportSelections) => void;
  onCancel: () => void;
}

/** Which sections the file carries at all; the dialog defaults each to exactly that. */
function initialSelections(data: ParsedImportData): ImportSelections {
  return {
    attackProfiles: data.atkProfiles !== null,
    targetTabs: data.tabs !== null && data.tabs.length > 0,
    targetTemplates: data.tgtTemplates !== null && Object.keys(data.tgtTemplates).length > 0,
    narrativeLogs: data.hasNarrative,
    roster: (data.roster?.length ?? 0) > 0,
    combatState: true,
    // Default to whatever the FILE actually carries, exactly as the sections
    // above do. A file with a map in it wants its map back.
    battlefield: !!data.hasBattlefield,
    mapLibrary: (data.mapCount ?? 0) > 0,
    tokenPositions: (data.tokenCount ?? 0) > 0,
    waitingRooms: (data.roomCount ?? 0) > 0,
    handouts: (data.handoutCount ?? 0) > 0,
  };
}

const NOT_IN_FILE = 'not in file';

/** "2 profiles, 5 weapons" - the weapons counted are the slots that hold one. */
function profilesCount(atkProfiles: AtkProfiles | null): string {
  const list = atkProfiles ? atkProfiles.list : {};
  const names = Object.keys(list);
  const weapons = names.reduce((sum, name) => sum + (list[name]?.filter((s) => !s.isEmpty).length ?? 0), 0);
  return `${countOf(names.length, 'profile')}, ${countOf(weapons, 'weapon')}`;
}

/** A section offered only when the file holds some of it: its count, or "not in file". */
function countedRow(
  key: keyof ImportSelections,
  id: string,
  label: string,
  n: number,
  count: string,
): SectionRowSpec<keyof ImportSelections> {
  return { key, id, label, available: n !== 0, count: n ? count : NOT_IN_FILE };
}

/** The v3 sections: attack profiles, target tabs and templates. */
function legacyRows(data: ParsedImportData, selectable: ImportSelections): SectionRowSpec<keyof ImportSelections>[] {
  const templateCount = data.tgtTemplates ? Object.keys(data.tgtTemplates).length : 0;
  return [
    {
      key: 'attackProfiles',
      id: 'imp-profiles',
      label: 'Attack Profiles',
      available: selectable.attackProfiles,
      count: selectable.attackProfiles ? profilesCount(data.atkProfiles) : NOT_IN_FILE,
    },
    {
      key: 'targetTabs',
      id: 'imp-tabs',
      label: 'Target Tabs',
      available: selectable.targetTabs,
      count: selectable.targetTabs ? countOf(data.tabs?.length ?? 0, 'tab') : NOT_IN_FILE,
    },
    {
      key: 'targetTemplates',
      id: 'imp-templates',
      label: 'Target Templates',
      available: selectable.targetTemplates,
      count: selectable.targetTemplates ? countOf(templateCount, 'template') : NOT_IN_FILE,
    },
  ];
}

/**
 * THE BATTLEFIELD. Offered at last: the importer always read these four
 * and the dialog never set them, so they were silently undefined and
 * every map and placement was thrown away.
 */
function battlefieldRows(data: ParsedImportData): SectionRowSpec<keyof ImportSelections>[] {
  const { mapCount = 0, tokenCount = 0, roomCount = 0, handoutCount = 0 } = data;
  return [
    {
      key: 'battlefield',
      id: 'imp-battlefield',
      label: 'Battlefield',
      available: !!data.hasBattlefield,
      count: data.hasBattlefield ? 'the map' : NOT_IN_FILE,
    },
    countedRow('tokenPositions', 'imp-tokens', 'Positions', tokenCount, `${tokenCount} placed`),
    countedRow('mapLibrary', 'imp-maplibrary', 'Map Library', mapCount, countOf(mapCount, 'map')),
    countedRow('waitingRooms', 'imp-rooms', 'Waiting Rooms', roomCount, countOf(roomCount, 'room')),
    {
      ...countedRow(
        'handouts',
        'imp-handouts',
        'Handouts (as drafts; files are not carried)',
        handoutCount,
        countOf(handoutCount, 'handout'),
      ),
      testId: 'import-handouts',
    },
  ];
}

/** The narrative, and the v4 roster: the roster supersedes the file's own v3 payload. */
function peopleRows(
  data: ParsedImportData,
  selectable: ImportSelections,
  rosterChosen: boolean,
): SectionRowSpec<keyof ImportSelections>[] {
  const rosterCount = data.roster?.length ?? 0;
  const narrativeCount = data.tabs?.reduce((sum, t) => sum + (t.narrativeLog?.length ?? 0), 0) ?? 0;
  return [
    {
      key: 'narrativeLogs',
      id: 'imp-narrative',
      label: 'Narrative Logs',
      available: selectable.targetTabs && data.hasNarrative,
      count: data.hasNarrative ? countOf(narrativeCount, 'entry', 'entries') : 'none in file',
    },
    {
      key: 'roster',
      id: 'imp-roster',
      label: 'Combatants',
      count: countOf(rosterCount, 'combatant'),
      show: rosterCount > 0,
    },
    // v4 only: a file may carry mid-battle wounds and conditions.
    // Unchecking starts everyone fresh from the same sheets.
    {
      key: 'combatState',
      id: 'imp-state',
      label: 'Combat State',
      available: rosterChosen,
      count:
        countOf(rosterCount, 'combatant') + (data.hasCombatState ? ' - wounded or afflicted' : ' - all at full health'),
      show: rosterCount > 0,
    },
  ];
}

function Warnings({ warnings }: { warnings: ValidationWarning[] }) {
  if (warnings.length === 0) return null;
  return (
    <div className={styles.warningBox}>
      <div className={styles.warningTitle}>{countOf(warnings.length, 'field')} normalized:</div>
      {warnings.slice(0, 10).map((w, i) => (
        <div key={i}>
          {w.field}: {w.message}
        </div>
      ))}
      {warnings.length > 10 && <div>...and {warnings.length - 10} more</div>}
    </div>
  );
}

export default function ImportDialog({ data, onImport, onCancel }: Props) {
  const backdrop = useModalBackdrop(onCancel);
  useEscapeKey(true, onCancel); // Escape cancels, as a click outside does (2026-10-23).
  // What the file carries is what may be chosen, and the initial choice.
  const selectable = initialSelections(data);
  const [selections, setSelections] = useState<ImportSelections>(() => initialSelections(data));
  const toggle = (key: keyof ImportSelections) => setSelections((prev) => ({ ...prev, [key]: !prev[key] }));

  // A v4 file has no readable v3 sections - its combatants ARE the people,
  // so without counting the roster here the Import button would sit
  // disabled on every file this version writes.
  const noneSelected =
    !selections.attackProfiles && !selections.targetTabs && !selections.targetTemplates && !selections.roster;
  const rows = [
    ...legacyRows(data, selectable),
    ...battlefieldRows(data),
    ...peopleRows(data, selectable, selections.roster),
  ];

  return (
    <div className={common.modal} onClick={backdrop.onBackdropClick} role="presentation">
      {/* A DIALOG SAYS SO. This was a bare div, alone among the modals, so a
          screen reader announced nothing and no test could address it by
          role - which is how an E2E for the import path never got written. */}
      <div
        className={common.modalContent}
        role="dialog"
        aria-modal="true"
        aria-label="Import session"
        ref={backdrop.boxRef}
      >
        <h2 className={styles.title}>Import Session Data</h2>
        <SectionRows rows={rows} selections={selections} onToggle={toggle} />
        <Warnings warnings={data.warnings} />
        <SectionFooter
          verb="Import"
          onCancel={onCancel}
          onConfirm={() => onImport(selections)}
          disabled={noneSelected}
        />
      </div>
    </div>
  );
}
